import React, { useRef, useState } from 'react'
import { Overlay, Popover } from 'react-bootstrap'
import { ColorResult, SliderPicker } from 'react-color'
import { useTranslation } from 'react-i18next'
import { randomColor } from '../../utils/color'
import './InputColor.scss'

interface InputColorProps {
  value: string
  onChange: (value: string) => void
  title?: string
  hint?: string
  error?: string
  disabled?: boolean
}

export const InputColor: React.FC<InputColorProps> = ({ value, onChange, title, hint, error, disabled = false }) => {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState(value || '#000000')
  const [showPicker, setShowPicker] = useState(false)
  const targetRef = useRef(null)

  const handleColorChange = (newColor: string) => {
    setInputValue(newColor)
    onChange(newColor)
  }

  const handlePickerChange = (color: ColorResult) => {
    handleColorChange(color.hex)
  }

  const handleRandomize = () => {
    const newColor = randomColor()
    handleColorChange(newColor)
  }

  const togglePicker = () => {
    if (!disabled) {
      setShowPicker(!showPicker)
    }
  }

  // Sync with external value changes
  React.useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '#000000')
    }
  }, [value])

  return (
    <div className={`mb-3 input-color ${disabled ? 'disabled' : ''}`}>
      {title && <label className="form-label">{title}</label>}

      <div className={`input-group ${error ? 'is-invalid' : ''}`}>
        <button
          ref={targetRef}
          type="button"
          className="input-group-text color-preview"
          style={{ backgroundColor: inputValue }}
          disabled={disabled}
          aria-label="Color preview"
          onClick={togglePicker}
        >
          &nbsp;&nbsp;&nbsp;
        </button>

        <input
          type="text"
          className={`form-control ${error ? 'is-invalid' : ''}`}
          value={inputValue}
          onChange={(e) => handleColorChange(e.target.value)}
          disabled={disabled}
          placeholder="#000000"
        />

        <button className="btn btn-outline-secondary" type="button" onClick={handleRandomize} disabled={disabled} title={t('random_color')}>
          <i className="bi bi-dice-5"></i>
        </button>
      </div>

      <Overlay show={showPicker} target={targetRef.current} placement="bottom" rootClose onHide={() => setShowPicker(false)}>
        <Popover id="color-picker-popover" className="shadow">
          <Popover.Body style={{ minWidth: '200px', padding: '12px' }}>
            <SliderPicker color={inputValue} onChangeComplete={handlePickerChange} />
          </Popover.Body>
        </Popover>
      </Overlay>

      {hint && !error && <small className="form-text text-muted">{hint}</small>}

      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  )
}
